import { useCallback, useEffect, useState } from "react";

/**
 * useMultiCooldown - one independent cooldown per key, counting down on
 * the button that owns it.
 *
 * PORTED FROM tracking-01, NOT INVENTED HERE (operator, 2026-08-22 :
 * "you could of done what tracking does like I originally asked", then
 * "they would have a file on how the button works too anyway" - they
 * do, `tracking-01/docs/COOLDOWN-MANAGEMENT.md`). That document's rule
 * 1 is "NEVER implement custom cooldown state in page components" and
 * rule 2 is "ALWAYS use the centralized hooks", which is why this is a
 * hook rather than more state inside ChallengePanel. The return shape
 * and the formatter semantics are deliberately theirs, so a reader who
 * knows the tracking system recognises this one on sight :
 *
 *   isItemInCooldown(key)        -> boolean
 *   getItemTimeRemaining(key)    -> whole seconds remaining
 *   startItemCooldown(key)       -> begin the full period for that key
 *   formatItemTimeRemaining(key) -> the string a button label carries
 *
 * ONE DELIBERATE DIVERGENCE : where they persist to localStorage, this
 * seeds from the SERVER. Our cooldowns are enforced in Valkey and
 * shared per challenge, so the authority already lives off the browser
 * - `seed` lets a caller plant deadlines from what the server reported
 * (see the challenge details' triggerCooldownRemainingMs). That is
 * stronger than their storage in the two cases their doc cannot cover :
 * a customer who reloads onto a different Cloud Run instance, and a
 * customer who opens the same session on a second machine. It is also
 * why the storage-pruning half of their hook has no counterpart here :
 * nothing of ours outlives the tab.
 *
 * @param {number} periodMs - the full cooldown length in MILLISECONDS.
 *   Their hook takes seconds ; ours takes milliseconds because every
 *   cooldown constant in `src/shared/issuance/config.js` is already
 *   expressed that way and converting at each call site is how the two
 *   units get mixed up.
 */
export function useMultiCooldown(periodMs) {
  // Deadlines as absolute wall-clock instants, never a decrementing
  // counter. A counter drifts when the tab is backgrounded and browsers
  // throttle timers, so a customer returning after two minutes would
  // read a wait that never elapsed. Their useCooldown stores the START
  // instant and recomputes from the clock on every tick ; storing the
  // END instant is the same guarantee with one less subtraction.
  const [deadlines, setDeadlines] = useState({});

  // The tick exists only to re-render : the remaining time is always
  // derived from Date.now() against the deadline, never stored.
  const [, forceTick] = useState(0);

  /**
   * Re-render once a second while any key is still cooling, and STOP
   * once none is.
   *
   * THE TICK MUST CHANGE `deadlines`, NOT ONLY FORCE A RENDER (round 3
   * of PR #106). The first version of this effect ticked a separate
   * state atom, which re-rendered but left `deadlines` identical - so
   * the effect never re-ran, its cleanup never fired, and the interval
   * outlived every countdown it was started for. Nothing looked wrong :
   * the label reached zero and the button re-enabled on schedule. The
   * cost was invisible and cumulative, a 1 Hz re-render per hook
   * instance for the rest of the visit, and Step 3 mounts one panel per
   * domain holding two instances each - twenty live intervals against
   * nothing on a ten-domain order whose panels have each seen a press.
   *
   * Dropping the past-due keys is what closes it : the map becomes a
   * new object, the effect re-runs, `anyRunning` is false, and the
   * cleanup clears the interval. Discarding them is safe because an
   * expired deadline and an absent one are the same answer to every
   * reader here, and `seed` plants a fresh one when the server reports
   * a new wait.
   */
  useEffect(() => {
    const anyRunning = Object.values(deadlines).some((at) => at > Date.now());
    if (!anyRunning) return undefined;
    const id = setInterval(() => {
      setDeadlines((prev) => {
        const now = Date.now();
        const live = {};
        let dropped = false;
        for (const [key, at] of Object.entries(prev)) {
          if (at > now) live[key] = at;
          else dropped = true;
        }
        // Identity is the signal : returning `prev` unchanged while
        // everything is still running is what keeps this effect from
        // re-running (and so from tearing down a healthy interval)
        // every single second.
        return dropped ? live : prev;
      });
      forceTick((n) => n + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [deadlines]);

  const getItemRemainingMs = useCallback(
    (key) => (key ? Math.max(0, (deadlines[key] || 0) - Date.now()) : 0),
    [deadlines]
  );

  const isItemInCooldown = useCallback(
    (key) => getItemRemainingMs(key) > 0,
    [getItemRemainingMs]
  );

  const getItemTimeRemaining = useCallback(
    (key) => Math.ceil(getItemRemainingMs(key) / 1000),
    [getItemRemainingMs]
  );

  const startItemCooldown = useCallback(
    (key) => {
      if (!key) return;
      setDeadlines((prev) => ({ ...prev, [key]: Date.now() + periodMs }));
    },
    [periodMs]
  );

  /**
   * Plant deadlines from remaining times the SERVER reported, so the
   * countdown is correct on first paint rather than only after the
   * customer presses something and is refused. Merges rather than
   * replaces : a locally-started cooldown must not be dropped by a
   * later refresh that happens to omit its key.
   *
   * @param {Record<string, number>} remainingMsByKey - only entries
   *   with a positive remaining time are planted ; a zero or absent
   *   value means "not cooling" and must not overwrite a live deadline
   *   with an expired one.
   */
  const seed = useCallback((remainingMsByKey) => {
    const now = Date.now();
    const fresh = {};
    for (const [key, ms] of Object.entries(remainingMsByKey || {})) {
      if (typeof ms === "number" && ms > 0) fresh[key] = now + ms;
    }
    if (Object.keys(fresh).length === 0) return;
    setDeadlines((prev) => {
      const next = { ...prev };
      for (const [key, at] of Object.entries(fresh)) {
        // Keep whichever expires LATER. The server is authoritative for
        // a cooldown it is enforcing, but a press made a moment ago may
        // not be reflected in a response already in flight.
        if (!next[key] || at > next[key]) next[key] = at;
      }
      return next;
    });
  }, []);

  const formatItemTimeRemaining = useCallback(
    (key) => formatCooldownTime(getItemTimeRemaining(key), periodMs),
    [getItemTimeRemaining, periodMs]
  );

  return {
    isItemInCooldown,
    getItemTimeRemaining,
    startItemCooldown,
    formatItemTimeRemaining,
    seed,
  };
}

/**
 * The cooldown string a button label carries.
 *
 * The choice of shape is keyed off the PERIOD, not off what is left -
 * tracking-01's `formatCooldownTime` does exactly this, and the reason
 * is that a button must not change shape as it counts down. A thirty
 * second wait reading "0:30" then "0:09" looks like a clock that has
 * stopped ; the same wait reading "30s" then "9s" reads as a short
 * pause. Conversely a five minute wait must never render "281s".
 *
 * @param {number} seconds - whole seconds remaining
 * @param {number} periodMs - the full cooldown length, in milliseconds
 */
export function formatCooldownTime(seconds, periodMs) {
  if (periodMs >= 60_000) {
    const minutes = Math.floor(seconds / 60);
    const secs = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${secs}`;
  }
  return `${seconds}s`;
}

/**
 * The label a cooling button shows : the ordinary label with the wait
 * in parentheses after it, e.g. "Request Validation (4:32)".
 *
 * tracking-01's EmailSendSection renders `${sendButtonText}
 * (${cooldownText})` and falls back to "(Please Wait...)" when it has
 * no figure. Keeping the ordinary label visible is the point : the
 * customer still reads what the button DOES, with the wait as an
 * annotation, rather than the label being replaced by a timer.
 */
export function cooldownLabel(label, waitText) {
  if (!waitText) return `${label} (Please Wait...)`;
  return `${label} (${waitText})`;
}

/**
 * useCooldown - a single unkeyed cooldown, for a button that has only
 * one instance on the page (Step 4's Check Progress, which is per session
 * rather than per challenge).
 *
 * tracking-01 ships the same pair, and for the same reason : threading
 * an invented key through a caller that has nothing to key on is how a
 * per-item hook gets misused. This is the keyed hook with one fixed
 * key, so there is a single implementation to reason about.
 */
const SOLE_KEY = "sole";

export function useCooldown(periodMs) {
  const {
    isItemInCooldown,
    getItemTimeRemaining,
    startItemCooldown,
    formatItemTimeRemaining,
    seed,
  } = useMultiCooldown(periodMs);

  // The two MUTATORS are memoised so their identity is stable across
  // renders. A caller wrapping its handler in useCallback has to list
  // whatever it calls, and a mutator re-created every render would
  // re-create that handler every render too - which is how a lint
  // warning gets silenced with a disable comment instead of fixed.
  // The READERS are deliberately NOT memoised : they must re-evaluate
  // on every tick, since that is the whole mechanism by which the
  // label counts down.
  const startCooldown = useCallback(
    () => startItemCooldown(SOLE_KEY),
    [startItemCooldown]
  );
  const seedRemainingMs = useCallback((ms) => seed({ [SOLE_KEY]: ms }), [seed]);

  return {
    isInCooldown: () => isItemInCooldown(SOLE_KEY),
    getTimeRemaining: () => getItemTimeRemaining(SOLE_KEY),
    formatTimeRemaining: () => formatItemTimeRemaining(SOLE_KEY),
    startCooldown,
    seedRemainingMs,
  };
}
